import axios, { AxiosResponse } from 'axios';
import { isEmpty } from 'lodash';
import { logger } from '@fiverr-private/obs';
import { countEventService } from '../../events/countEvent';

export interface InvitationData {
    invitationId: string;
    organizationId: string;
    recipientEmail: string;
    senderEmail: string;
    shortUrl: string;
    organizationProfile: {
        name: string;
        companyName: string;
        profileImageUrl: string;
        organizationId: string;
    };
}

export interface InvitationDataResponse {
    invitationData: InvitationData | null;
}

const isBrowser = () => typeof window !== 'undefined';

const hasInvitationData = (data: InvitationData) => !isEmpty(data) && data.invitationId && data.organizationId;

/**
 * @param params
 * @param params.businessAccountsPhoenixServiceUrl - http://${process.env.GENERAL_SERVICES_BUSINESS_ACCOUNTS_SERVICE}/api/v2
 */
export const getInvitationData = async ({
    email,
    source = '',
    businessAccountsPhoenixServiceUrl,
}: {
    email: string;
    source?: string;
    businessAccountsPhoenixServiceUrl?: string;
}): Promise<InvitationDataResponse> => {
    let request: () => Promise<AxiosResponse<InvitationData>>;

    if (businessAccountsPhoenixServiceUrl && !isBrowser()) {
        const url = `${businessAccountsPhoenixServiceUrl}/internal/invitation`;
        request = () => axios.get<InvitationData>(url, { params: { invitee_email: email } });
    } else {
        request = () =>
            axios.get<InvitationData>('/business_acquisition/ajax/check_invitation', {
                params: { email },
            });
    }

    try {
        const response = await request();

        countEventService({ metric: 'get_invitation_data', status: 'success', source });

        return {
            invitationData: hasInvitationData(response?.data) ? response.data : null,
        };
    } catch (error) {
        logger.error(error as any, {
            message: `Error getting invitation data`,
            flow: 'getInvitationData',
            businessAccountsPhoenixServiceUrl,
            email,
            source,
        });
        countEventService({ metric: 'get_invitation_data', status: 'fail', source });
        throw error;
    }
};
